/**
 * CapacityLoadRing.tsx
 * A small SVG donut showing a person's TOTAL weekly load (Σ this plan + other
 * plans) for one (person, week) cell in the "Staff this job" capacity table
 * (STAFF-VIEW-V2 cell redesign 2026-07-14). Replaces the top stacked bar
 * (AllocationBandBar) inside CapacityWeekCell — it is the glanceable, READ-ONLY
 * "overall load" signal, colored by utilization band (under=blue / healthy=green
 * / at=amber / over=red) with the % in the center.
 *
 * Over-100% (over) pins the arc visually at full but reads red — the real % is
 * still printed in the center. Band 'none' (0% / stale) shows an empty track and
 * an em dash.
 *
 * PURE / presentational: the geometry + labels come from the exported
 * computeRingGeometry() (unit-tested); the component only maps band → theme
 * token color and draws the SVG. Reduced-motion safe (the arc has a short width
 * transition, disabled under prefers-reduced-motion — never a spin). Colors via
 * theme.schedule.alloc* tokens — no hex literals (CODING-STANDARDS).
 */

import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  allocationBandLabel,
  allocationPercentLabel,
  type AllocationBand,
} from '../../../../utils/scheduleAllocationBands';

export interface CapacityLoadRingProps {
  /** Σ TOTAL utilization % (this plan + other plans). undefined ⇒ stale → em dash. */
  percent: number | undefined;
  /** The utilization band (drives the ring color + word). */
  band: AllocationBand;
  /** Extra tooltip context, e.g. "busy 3 of 5 days" (optional). */
  subLabel?: string | null;
  /** Ring diameter in px (default 44). */
  size?: number;
  /**
   * THIS job's share of the week (the editable number beside the ring). When
   * provided the tooltip spells out the total → this-job vs elsewhere split, so a
   * full ring next to "0% this job" reads as "already busy elsewhere", not a
   * glitch (BUG-2026-0141 #3). Omitted in the legacy Classic table.
   */
  thisJobPercent?: number | null;
}

/**
 * Pure tooltip text for the ring. The ring is ALWAYS the person's TOTAL weekly
 * load across every job; when the this-job share is known we append the split
 * ("100% total across all jobs — 0% this job, 100% elsewhere").
 */
export function ringTooltip(
  percent: number | undefined,
  band: AllocationBand,
  thisJobPercent: number | null | undefined,
  subLabel: string | null | undefined,
): string {
  if (band === 'none') {
    return 'No allocation this week' + (subLabel ? ` · ${subLabel}` : '');
  }
  const total = typeof percent === 'number' && Number.isFinite(percent) ? Math.round(percent) : 0;
  let text = `${allocationPercentLabel(percent)} total weekly load across all jobs (${allocationBandLabel(band)})`;
  if (typeof thisJobPercent === 'number' && Number.isFinite(thisJobPercent)) {
    const here = Math.round(thisJobPercent);
    const elsewhere = Math.max(0, total - here);
    text += ` — ${here}% this job, ${elsewhere}% other jobs`;
  }
  if (subLabel) text += ` · ${subLabel}`;
  return text;
}

/** Compact band word shown under the ring ('' for 'none'). */
export function ringBandWord(band: AllocationBand): string {
  switch (band) {
    case 'none':    return '';
    case 'light':   return 'under';
    case 'healthy': return 'healthy';
    case 'at':      return 'at capacity';
    case 'over':    return 'over';
  }
}

export interface RingGeometry {
  /** Arc fill 0–100 (capped; 'over' pins full at 100). */
  fillPct: number;
  /** Center text — "—" for 'none', else "125%". */
  centerLabel: string;
  /** Compact word under the ring ('' for 'none'). */
  bandWord: string;
}

/**
 * Pure percent + band → ring geometry (arc fill %, center label, band word).
 * The arc is capped at 100 (an over-allocated person pins the ring full but
 * reads red via the band color + shows the true % in the center).
 */
export function computeRingGeometry(
  percent: number | undefined,
  band: AllocationBand,
): RingGeometry {
  if (band === 'none') {
    return { fillPct: 0, centerLabel: allocationPercentLabel(percent), bandWord: '' };
  }
  const p = typeof percent === 'number' && Number.isFinite(percent) ? percent : 0;
  const fillPct = Math.max(0, Math.min(100, Math.round(p)));
  return {
    fillPct,
    centerLabel: allocationPercentLabel(percent),
    bandWord: ringBandWord(band),
  };
}

export const CapacityLoadRing: React.FC<CapacityLoadRingProps> = ({
  percent,
  band,
  subLabel,
  size = 44,
  thisJobPercent,
}) => {
  const theme = useTheme();

  const colorByBand: Record<Exclude<AllocationBand, 'none'>, string> = {
    light: theme.schedule.allocUnder,
    healthy: theme.schedule.allocHealthy,
    at: theme.schedule.allocAt,
    over: theme.schedule.allocOver,
  };

  const { fillPct, centerLabel, bandWord } = computeRingGeometry(percent, band);
  const color = band === 'none' ? theme.palette.text.disabled : colorByBand[band];

  // SVG donut geometry.
  const stroke = Math.max(3, Math.round(size * 0.11));
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const circumference = 2 * Math.PI * r;
  const dashOffset = circumference * (1 - fillPct / 100);

  const tooltip = ringTooltip(percent, band, thisJobPercent, subLabel);

  return (
    <Tooltip title={tooltip} placement="top" arrow>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.15 }}>
        <Box sx={{ position: 'relative', width: size, height: size }}>
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={tooltip}>
            {/* Track — faint neutral ring. */}
            <circle
              cx={cx}
              cy={cx}
              r={r}
              fill="none"
              stroke={alpha(color, band === 'none' ? 0.18 : 0.16)}
              strokeWidth={stroke}
            />
            {/* Value arc — starts at top (rotate -90°), grows clockwise. */}
            {fillPct > 0 && (
              <circle
                cx={cx}
                cy={cx}
                r={r}
                fill="none"
                stroke={color}
                strokeWidth={stroke}
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={dashOffset}
                transform={`rotate(-90 ${cx} ${cx})`}
                style={{ transition: 'stroke-dashoffset 0.2s ease' }}
              />
            )}
          </svg>
          {/* Centered % — absolutely positioned over the SVG. */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              // Reduced-motion safe: the arc transition above is a width tween,
              // not a spin; disable it entirely for reduced-motion users.
              '@media (prefers-reduced-motion: reduce)': { '& *': { transition: 'none !important' } },
            }}
          >
            <Typography
              sx={{
                fontSize: size <= 40 ? '0.62rem' : '0.68rem',
                fontWeight: 800,
                lineHeight: 1,
                color,
              }}
            >
              {centerLabel}
            </Typography>
          </Box>
        </Box>
        {bandWord && (
          <Typography
            sx={{
              fontSize: '0.55rem',
              fontWeight: 600,
              lineHeight: 1,
              color: alpha(color, 0.95),
              textTransform: 'lowercase',
              textAlign: 'center',
            }}
          >
            {bandWord}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};
